import './index.css'
import { Theme } from '@radix-ui/themes'
import cn from 'clsx'
import * as React from 'react'
import Modal from 'react-modal'
import InlineSVG from 'svg-inline-react'
import Button from 'components/ui/button'
import { HoverContext } from 'components/ui/hover-provider'
import { hamburgerModalStyles } from 'components/utils/modalStyles'
type Props = {
  children: (props: Record<string, any>) => any
  appearance?: 'default' | 'action-menu'
  buttonClassName?: string
  menuClassName?: string
  onMenuOpen?: (...args: Array<any>) => any
  getMenuOffset: (top: number) => string[]
  customButton?: React.ReactElement<any>
  // Temporary hack
  buttonProps?: Record<string, any>
  maxHeight?: number
}
type OwnState = {
  menuOpen: boolean
  styles: Record<string, any>
  submenuPosition: 'right' | 'left'
}

function defaultGetMenuOffset(top: number) {
  // TODO: rewrite `defaultGetMenuOffset`
  // TODO: use defaultGetMenuOffset in all <ContextMenu>
  const biggestMenuHeight = 450
  const approximateMenuHeight = 380
  const windowHeight = window.innerHeight

  if (
    document.body &&
    document.body.className &&
    document.body.className.includes('is-phone')
  ) {
    // mobile
    if (top > windowHeight / 2) {
      return ['calc(-100% + 1rem)', '-10.5rem']
    } else {
      return ['calc(-100% + 1rem)', '1rem']
    }
  } else {
    // desktop
    if (top > windowHeight / 2) {
      return [
        'calc(-100% + 1rem)',
        `${Math.max(-approximateMenuHeight, 10 - top)}px`,
      ]
    } else {
      return [
        'calc(-100% + 1rem)',
        `${Math.min(10, windowHeight - biggestMenuHeight - top)}px`,
      ]
    }
  }
}

/**
 * @deprecated Use DropdownMenu from components/ds instead
 *
 * Context menu component that displays a dropdown menu with various actions
 * Supports custom buttons, positioning, and mobile-responsive layouts
 */
class ContextMenu extends React.Component<Props, OwnState> {
  static contextType = HoverContext
  declare context: React.ContextType<typeof HoverContext>
  static defaultProps: {
    getMenuOffset: (top: number) => Array<string>
  } = {
    getMenuOffset: defaultGetMenuOffset,
  }
  state: OwnState = {
    menuOpen: false,
    styles: {
      content: {},
      overlay: {},
    },
    submenuPosition: 'left',
  }
  _container: HTMLElement | null | undefined
  _closeMenu: (
    e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => void = (
    e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    if (e) e.stopPropagation()

    if (this.context.setHover) {
      this.context.setHover(false)
    }

    this.setState({
      menuOpen: false,
    })
  }
  _toggleMenuOpen: (e: React.SyntheticEvent<EventTarget>) => void = (
    e: React.SyntheticEvent<any>,
  ) => {
    e.stopPropagation()
    if (!this._container) return
    if (!this.state.menuOpen && this.props.onMenuOpen) this.props.onMenuOpen()

    const { left, top, right } = this._container.getBoundingClientRect()

    const styles = {
      content: { ...hamburgerModalStyles.content },
      overlay: { ...hamburgerModalStyles.overlay },
    }
    if (this.props.maxHeight != null && this.props.maxHeight > 0) {
      styles.content.maxHeight = this.props.maxHeight
      styles.content.overflow = 'auto'
    }

    styles.content.left = left
    styles.content.top = top
    const [xOffset, yOffset] = this.props.getMenuOffset(top)
    styles.content.transform = `translate(${xOffset}, ${yOffset})`
    this.setState({
      menuOpen: !this.state.menuOpen,
      styles: styles,
      submenuPosition: right < window.innerWidth - 100 ? 'right' : 'left',
    })
  }

  render(): React.ReactElement<'div'> {
    const buttonClassName = cn('context-menu', this.props.buttonClassName, {
      'context-menu--custom': this.props.customButton,
    })
    // TODO: move `arrowDownIcon` out of context-menu
    const arrowDownIcon = (
      <InlineSVG
        src={require('!svg-inline!assets/icons/arrow-down.svg')}
        width="24"
        height="24"
        raw
      />
    )
    const button = this.props.customButton ? (
      React.cloneElement(this.props.customButton, {
        className: buttonClassName,
        onClick: this._toggleMenuOpen,
      })
    ) : this.props.appearance === 'action-menu' ? (
      <Button
        caption="Actions"
        split={arrowDownIcon}
        onClick={this._toggleMenuOpen}
        aria-label="More actions"
        {...this.props.buttonProps}
      />
    ) : (
      <Button
        appearance="icon"
        compact
        icon="ellipsis-vertical"
        onClick={this._toggleMenuOpen}
        aria-label="More actions"
        {...this.props.buttonProps}
      />
    )
    return (
      <div
        ref={r => {
          this._container = r
        }}
        data-testid="context-menu-trigger"
      >
        {button}
        {this.state.menuOpen && (
          <Modal
            className={this.props.menuClassName}
            onRequestClose={this._closeMenu}
            isOpen
            style={this.state.styles}
          >
            <Theme>
              {this.props.children({
                closeMenu: this._closeMenu,
                submenuPosition: this.state.submenuPosition,
              })}
            </Theme>
          </Modal>
        )}
      </div>
    )
  }
}

export default ContextMenu
